import React, { useState, useEffect } from 'react';
import { X, Sparkles, Loader2, CheckCircle, Plus, Trash2, Calendar, Clock, User, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = '/api';

const BulkTaskGenerationModal = ({ caseId, onClose, onTasksCreated }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;
  
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [editedTasks, setEditedTasks] = useState([]);
  const [creating, setCreating] = useState(false);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    fetchSuggestions();
    fetchMembers();
  }, [caseId]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai/tasks/suggest-bulk-tasks`,
        { case_id: caseId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const suggested = response.data || [];
      
      // Convert to editable format
      const today = new Date();
      const tasksWithDates = suggested.map((task, index) => ({
        id: `temp-${index}`,
        title: task.title,
        description: task.description,
        estimated_hours: task.estimated_hours,
        suggested_assignee_role: task.suggested_assignee_role,
        due_date: new Date(today.getTime() + task.due_date_offset_days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        reasoning: task.reasoning,
        priority: 'MEDIUM',
        assignee_user_id: null,
        included: true
      }));

      setEditedTasks(tasksWithDates);
    } catch (error) {
      console.error('Fetch suggestions error:', error);
      toast.error(t('Could not generate task suggestions', 'Kunne ikke generere oppgaveforslag'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/cases/${caseId}/members`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const updateTask = (id, field, value) => {
    setEditedTasks(prev => 
      prev.map(task => 
        task.id === id ? { ...task, [field]: value } : task
      )
    );
  };

  const removeTask = (id) => {
    setEditedTasks(prev => prev.filter(task => task.id !== id));
  };

  const toggleInclude = (id) => {
    setEditedTasks(prev =>
      prev.map(task =>
        task.id === id ? { ...task, included: !task.included } : task
      )
    );
  };

  const handleCreateAll = async () => {
    const tasksToCreate = editedTasks.filter(t => t.included);
    
    if (tasksToCreate.length === 0) {
      toast.error(t('Select at least one task to create', 'Velg minst én oppgave å opprette'));
      return;
    }

    setCreating(true);

    try {
      const token = localStorage.getItem('token');
      
      // Create tasks sequentially (could be parallel but this ensures order)
      for (const task of tasksToCreate) {
        const payload = {
          title: task.title,
          description: task.description,
          estimated_hours: task.estimated_hours,
          due_date: task.due_date ? `${task.due_date}T12:00:00Z` : null,
          priority: task.priority,
          assignee_user_id: task.assignee_user_id || null,
          source: 'TEMPLATE',
          ai_reasoning: task.reasoning
        };

        await axios.post(
          `${API}/tasks/?case_id=${caseId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success(t(`✅ ${tasksToCreate.length} tasks created!`, `✅ ${tasksToCreate.length} oppgaver opprettet!`));
      onTasksCreated();
    } catch (error) {
      console.error('Create tasks error:', error);
      toast.error(t('Could not create all tasks', 'Kunne ikke opprette alle oppgaver'));
    } finally {
      setCreating(false);
    }
  };

  const totalHours = editedTasks
    .filter(t => t.included)
    .reduce((sum, t) => sum + (t.estimated_hours || 0), 0);

  const includedCount = editedTasks.filter(t => t.included).length;

  const getRoleColor = (role) => {
    const colors = {
      LEAD: 'bg-purple-100 text-purple-700',
      ASSOCIATE: 'bg-blue-100 text-blue-700',
      PARALEGAL: 'bg-green-100 text-green-700',
      EXPERT: 'bg-orange-100 text-orange-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('AI-generated task plan', 'AI-generert oppgaveplan')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('Based on case type and complexity', 'Basert på sakens type og kompleksitet')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Summary Bar */}
          {!loading && (
            <div className="mt-4 flex items-center gap-4 p-3 bg-white rounded-lg border border-indigo-200">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-600" />
                <span className="text-sm font-semibold text-gray-900">
                  {t(`${includedCount} of ${editedTasks.length} tasks`, `${includedCount} av ${editedTasks.length} oppgaver`)}
                </span>
              </div>
              <div className="w-px h-6 bg-gray-300" />
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-indigo-600" />
                <span className="text-sm font-semibold text-gray-900">
                  {t(`${totalHours.toFixed(1)}h total estimated`, `${totalHours.toFixed(1)}t totalt estimert`)}
                </span>
              </div>
              <div className="w-px h-6 bg-gray-300" />
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" />
                <span className="text-sm text-gray-600">
                  {editedTasks.length > 0 && editedTasks[editedTasks.length - 1].due_date
                    ? t(`End date: ${new Date(editedTasks[editedTasks.length - 1].due_date).toLocaleDateString(isEnglish ? 'en-US' : 'no-NO')}`, `Sluttdato: ${new Date(editedTasks[editedTasks.length - 1].due_date).toLocaleDateString('no-NO')}`)
                    : t('Varying deadlines', 'Varierende frister')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mb-4" />
              <p className="text-gray-600">{t('Generating task plan with AI...', 'Genererer oppgaveplan med AI...')}</p>
            </div>
          ) : editedTasks.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">{t('No tasks generated', 'Ingen oppgaver generert')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {editedTasks.map((task, index) => (
                <div
                  key={task.id}
                  className={`border-2 rounded-lg p-4 transition-all ${
                    task.included
                      ? 'border-indigo-200 bg-white'
                      : 'border-gray-200 bg-gray-50 opacity-50'
                  }`}
                >
                  {/* Task Header */}
                  <div className="flex items-start gap-3 mb-3">
                    <input
                      type="checkbox"
                      checked={task.included}
                      onChange={() => toggleInclude(task.id)}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-gray-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-500">#{index + 1}</span>
                        <input
                          type="text"
                          value={task.title}
                          onChange={(e) => updateTask(task.id, 'title', e.target.value)}
                          className="flex-1 text-lg font-semibold text-gray-900 border-b border-transparent hover:border-gray-300 focus:border-indigo-500 focus:outline-none px-2 py-1"
                          disabled={!task.included}
                        />
                      </div>

                      <textarea
                        value={task.description}
                        onChange={(e) => updateTask(task.id, 'description', e.target.value)}
                        rows={2}
                        className="w-full text-sm text-gray-600 border border-gray-200 rounded px-3 py-2 focus:outline-none focus:border-indigo-500 resize-none"
                        placeholder="Beskrivelse..."
                        disabled={!task.included}
                      />

                      {/* AI Reasoning */}
                      {task.reasoning && (
                        <div className="mt-2 p-2 bg-gray-50 border border-indigo-100 rounded text-xs text-indigo-700">
                          💡 {task.reasoning}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => removeTask(task.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('Remove task', 'Fjern oppgave')}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>

                  {/* Task Details */}
                  <div className="grid grid-cols-4 gap-3 mt-3 pl-8">
                    {/* Estimated Hours */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('Estimate', 'Estimat')}
                      </label>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={task.estimated_hours}
                          onChange={(e) => updateTask(task.id, 'estimated_hours', parseFloat(e.target.value))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-indigo-500"
                          disabled={!task.included}
                        />
                        <span className="text-xs text-gray-500">{t('h', 't')}</span>
                      </div>
                    </div>

                    {/* Due Date */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('Deadline', 'Frist')}
                      </label>
                      <input
                        type="date"
                        value={task.due_date}
                        onChange={(e) => updateTask(task.id, 'due_date', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-indigo-500"
                        disabled={!task.included}
                      />
                    </div>

                    {/* Suggested Role */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('Recommended role', 'Anbefalt rolle')}
                      </label>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getRoleColor(task.suggested_assignee_role)}`}>
                        {task.suggested_assignee_role}
                      </span>
                    </div>

                    {/* Assignee */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('Assign now (optional)', 'Tildel nå (valgfri)')}
                      </label>
                      <select
                        value={task.assignee_user_id || ''}
                        onChange={(e) => updateTask(task.id, 'assignee_user_id', e.target.value || null)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-indigo-500"
                        disabled={!task.included}
                      >
                        <option value="">{t('Not assigned', 'Ikke tildelt')}</option>
                        {members
                          .filter(m => m.role === task.suggested_assignee_role || m.role === 'LAWYER')
                          .map(member => (
                            <option key={member.user_id} value={member.user_id}>
                              {member.user_name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && editedTasks.length > 0 && (
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {includedCount === editedTasks.length
                  ? t('All tasks will be created', 'Alle oppgaver vil bli opprettet')
                  : t(`${includedCount} of ${editedTasks.length} tasks will be created`, `${includedCount} av ${editedTasks.length} oppgaver vil bli opprettet`)}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={onClose}
                  variant="outline"
                  disabled={creating}
                >
                  {t('Cancel', 'Avbryt')}
                </Button>
                <Button
                  onClick={handleCreateAll}
                  disabled={creating || includedCount === 0}
                  className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('Creating...', 'Oppretter...')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t(`Create ${includedCount} tasks`, `Opprett ${includedCount} oppgaver`)}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkTaskGenerationModal;
